import React, { useState, useEffect, useCallback } from 'react';
import { supabase, insertRow, updateRow } from '../../db/engine';
import { etiquetaEstadoPoliza } from '../../db/renovaciones';
import { useApp } from '../../context/AppContext';
import { abrirDocumento } from '../../utils/archivos';
import { coincideBusqueda } from '../../utils/buscar';
import { Plus, Search, X, UserCheck, Edit2, FileText } from 'lucide-react';
import { fmtFecha } from '../../utils/fecha';

import { fmtMoneda as fmt$ } from '../../utils/moneda';
import { getAbrev } from '../../utils/marca';
const ESTADO_BADGE = { activa:'badge-success', vencida:'badge-danger', cancelada:'badge-neutral', en_renovacion:'badge-warning', borrador:'badge-info', no_renovada:'badge-neutral' };

const EMPTY = { nombre:'', email:'', telefono:'', cedula:'', dip_vencimiento:'', tipo:'activo', comision_nueva_agente:50, comision_nueva_ru:50, comision_renovacion_agente:50, comision_renovacion_ru:50, activo:true };

export default function Agentes() {
  const { canEdit } = useApp();
  const abrev = getAbrev();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [polizasAgente, setPolizasAgente] = useState([]);
  const [loadingPolizas, setLoadingPolizas] = useState(false);
  const [linkedUser, setLinkedUser] = useState(null); // usuario vinculado a este agente (si lo hay)

  const load = useCallback(async () => {
    let q = supabase.from('agentes').select('*').order('nombre');
    let { data } = await q;
    // Búsqueda por palabras sueltas, sin acentos y en cualquier orden (igual que ClienteSelect).
    if (search) data = (data || []).filter(a => coincideBusqueda(`${a.nombre||''} ${a.email||''}`, search));
    // Conteo de pólizas activas por agente
    const ids = (data || []).map(a => a.id);
    let counts = {};
    if (ids.length) {
      const { data: pols } = await supabase.from('polizas').select('agente_id').eq('estado','activa').in('agente_id', ids);
      (pols || []).forEach(p => { if (p.agente_id) counts[p.agente_id] = (counts[p.agente_id] || 0) + 1; });
    }
    setRows((data || []).map(a => ({ ...a, total_polizas: counts[a.id] || 0 })));
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY); setLinkedUser(null); setModal('create'); };
  const openEdit = async (r) => {
    setSelected(r); setForm({ ...r }); setLinkedUser(null); setModal('edit');
    // Si hay un usuario vinculado a este agente, la cédula se gestiona desde el usuario.
    const { data: u } = await supabase.from('usuarios')
      .select('id, nombre, cedula, cedula_url, dip_vencimiento')
      .eq('agente_id', r.id).eq('activo', true).maybeSingle();
    if (u) setLinkedUser(u);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) return;
    const agNueva = form.comision_nueva_agente !== '' && form.comision_nueva_agente != null ? parseFloat(form.comision_nueva_agente) : 50;
    const agRenov = form.comision_renovacion_agente !== '' && form.comision_renovacion_agente != null ? parseFloat(form.comision_renovacion_agente) : 50;
    const data = {
      nombre:form.nombre, email:form.email||null, telefono:form.telefono||null,
      cedula:form.cedula||null, dip_vencimiento: form.dip_vencimiento || null,
      tipo:form.tipo, activo: form.activo === false ? false : true,
      comision_nueva_agente: agNueva, comision_nueva_ru: 100 - agNueva,
      comision_renovacion_agente: agRenov, comision_renovacion_ru: 100 - agRenov
    };
    try {
      if (modal === 'create') {
        await insertRow('agentes', data);
      } else {
        await updateRow('agentes', selected.id, data);

        // ── Cascada: actualizar poliza_agentes no manuales ────────────
        const cambioNueva = agNueva !== parseFloat(selected.comision_nueva_agente);
        const cambioRenov = agRenov !== parseFloat(selected.comision_renovacion_agente);

        if (cambioNueva || cambioRenov) {
          // Obtener IDs de pólizas nueva vs renovación para este agente
          if (cambioNueva) {
            const { data: polNuevas } = await supabase
              .from('polizas').select('id').eq('tipo', 'nueva');
            const ids = (polNuevas || []).map(p => p.id);
            if (ids.length) {
              await supabase.from('poliza_agentes')
                .update({ porcentaje: agNueva })
                .eq('agente_id', selected.id)
                .eq('comision_manual', false)
                .in('poliza_id', ids);
            }
          }
          if (cambioRenov) {
            const { data: polRenov } = await supabase
              .from('polizas').select('id').eq('tipo', 'renovacion');
            const ids = (polRenov || []).map(p => p.id);
            if (ids.length) {
              await supabase.from('poliza_agentes')
                .update({ porcentaje: agRenov })
                .eq('agente_id', selected.id)
                .eq('comision_manual', false)
                .in('poliza_id', ids);
            }
          }
        }
      }
      await load(); setModal(null);
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const openPolizas = async (agente) => {
    setSelected(agente);
    setLoadingPolizas(true);
    setModal('polizas');
    const { data } = await supabase
      .from('poliza_agentes')
      .select('porcentaje, comision_manual, polizas(id, numero, tipo, estado, prima, fecha_inicio, fecha_vencimiento, clientes(nombre), ramos(nombre), aseguradoras(nombre))')
      .eq('agente_id', agente.id)
      .order('poliza_id', { ascending: false });
    setPolizasAgente((data || []).map(pa => ({
      ...pa.polizas,
      porcentaje: pa.porcentaje,
      comision_manual: pa.comision_manual,
      cliente:     pa.polizas?.clientes?.nombre,
      ramo:        pa.polizas?.ramos?.nombre,
      aseguradora: pa.polizas?.aseguradoras?.nombre,
    })));
    setLoadingPolizas(false);
  };

  const toggleActivo = async (r) => {
    try { await updateRow('agentes', r.id, { activo: !r.activo }); await load(); }
    catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Agentes</div>
          <div className="page-subtitle">{rows.filter(r => r.activo).length} activos · {rows.filter(r => !r.activo).length} inactivos</div>
        </div>
        {canEdit('agentes') && <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> Nuevo Agente</button>}
      </div>

      <div className="toolbar">
        <div className="search-bar" style={{ flex:1, maxWidth:400 }}>
          <Search size={16} style={{ color:'var(--silver)', flexShrink:0 }} />
          <input placeholder="Buscar agente..." value={search} onChange={e=>setSearch(e.target.value)} />
        </div>
      </div>

      <div className="table-wrapper">
        <table>
          <thead><tr><th>Nombre</th><th>Tipo</th><th>Teléfono</th><th>Email</th><th>Vence DIP</th><th>% Nueva (Ag/{abrev})</th><th>% Renov. (Ag/{abrev})</th><th>Pólizas Activas</th><th>Estado</th><th>Acciones</th></tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={10}><div className="empty-state"><div className="empty-state-icon"><UserCheck size={48} /></div><div className="empty-state-title">Sin agentes</div></div></td></tr>
            ) : rows.map(r => (
              <tr key={r.id} style={{ opacity: r.activo ? 1 : 0.5 }}>
                <td style={{ fontWeight:600 }}>{r.nombre}</td>
                <td><span className={`badge ${r.tipo === 'activo' ? 'badge-success' : 'badge-warning'}`}>{r.tipo}</span></td>
                <td className="text-silver">{r.telefono || '—'}</td>
                <td className="text-silver">{r.email || '—'}</td>
                <td>{(() => {
                  if (!r.dip_vencimiento) return <span className="text-silver">—</span>;
                  const hoy = new Date().toISOString().slice(0,10);
                  const dias = Math.ceil((new Date(r.dip_vencimiento) - new Date(hoy)) / 86400000);
                  if (dias < 0)  return <span className="badge badge-danger" title="DIP vencido">Vencido · {fmtFecha(r.dip_vencimiento)}</span>;
                  if (dias <= 60) return <span className="badge badge-warning" title={`Vence en ${dias} días`}>{fmtFecha(r.dip_vencimiento)}</span>;
                  return <span className="text-silver">{fmtFecha(r.dip_vencimiento)}</span>;
                })()}</td>
                <td className="text-sky">{r.comision_nueva_agente}% / {r.comision_nueva_ru}%</td>
                <td className="text-sky">{r.comision_renovacion_agente}% / {r.comision_renovacion_ru}%</td>
                <td><span className="badge badge-info">{r.total_polizas || 0}</span></td>
                <td><span className={`badge ${r.activo ? 'badge-success' : 'badge-neutral'}`}>{r.activo ? 'Activo' : 'Inactivo'}</span></td>
                <td>
                  <div style={{ display:'flex', gap:6 }}>
                    <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>openPolizas(r)} title="Ver pólizas"><FileText size={14} /></button>
                    {canEdit('agentes') && (
                      <>
                        <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>openEdit(r)}><Edit2 size={14} /></button>
                        <button className={`btn btn-sm ${r.activo ? 'btn-danger' : 'btn-success'}`} onClick={()=>toggleActivo(r)}>{r.activo ? 'Desactivar' : 'Activar'}</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ══ Modal: Pólizas del agente ══════════════════════════════ */}
      {modal === 'polizas' && selected && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal modal-lg" style={{ maxWidth:900 }} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Pólizas — {selected.nombre}</div>
                <div className="text-silver text-sm">
                  Nueva: {selected.comision_nueva_agente}% · Renovación: {selected.comision_renovacion_agente}%
                </div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModal(null)}><X size={16}/></button>
            </div>
            <div className="modal-body" style={{ maxHeight:'70vh', overflowY:'auto' }}>
              {loadingPolizas ? (
                <div className="text-silver text-sm" style={{ textAlign:'center', padding:32 }}>Cargando pólizas...</div>
              ) : polizasAgente.length === 0 ? (
                <div className="text-silver text-sm" style={{ textAlign:'center', padding:32 }}>
                  Este agente no tiene pólizas registradas.
                </div>
              ) : (
                <>
                  <div style={{ marginBottom:12, fontSize:13, color:'var(--silver)' }}>
                    {polizasAgente.length} póliza{polizasAgente.length !== 1 ? 's' : ''} · Prima total: <strong style={{ color:'var(--sky-light)' }}>{fmt$(polizasAgente.reduce((s,p) => s + (parseFloat(p.prima)||0), 0))}</strong>
                  </div>
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          <th>N° Póliza</th>
                          <th>Cliente</th>
                          <th>Ramo</th>
                          <th>Aseguradora</th>
                          <th>Prima</th>
                          <th>% Comisión</th>
                          <th>Vigencia</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {polizasAgente.map((p, i) => (
                          <tr key={i}>
                            <td style={{ fontWeight:700, color:'var(--sky)' }}>{p.numero || '—'}</td>
                            <td style={{ fontWeight:600 }}>{p.cliente || '—'}</td>
                            <td style={{ fontSize:12 }}>{p.ramo || '—'}</td>
                            <td className="text-silver" style={{ fontSize:12 }}>{p.aseguradora || '—'}</td>
                            <td style={{ color:'var(--text-success)', fontWeight:600 }}>{p.prima ? fmt$(p.prima) : '—'}</td>
                            <td style={{ textAlign:'center' }}>
                              <span style={{ fontSize:12 }}>{p.porcentaje != null ? `${p.porcentaje}%` : '—'}</span>
                              {p.comision_manual && <span style={{ marginLeft:5, fontSize:10, color:'var(--text-warning)' }}>manual</span>}
                            </td>
                            <td style={{ fontSize:12, color:'var(--silver)' }}>
                              {fmtFecha(p.fecha_inicio) || '—'}<br/>
                              <span style={{ fontSize:11 }}>{fmtFecha(p.fecha_vencimiento)}</span>
                            </td>
                            <td>
                              <span className={`badge ${ESTADO_BADGE[p.estado] || 'badge-neutral'}`}>{etiquetaEstadoPoliza(p.estado) || '—'}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {(modal === 'create' || modal === 'edit') && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">{modal === 'create' ? 'Nuevo Agente' : 'Editar Agente'}</div><button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModal(null)}><X size={16} /></button></div>
            <div className="modal-body">
              <div className="form-grid form-grid-2">
                <div className="form-group" style={{ gridColumn:'span 2' }}><label className="form-label">Nombre completo *</label><input className="form-input" value={form.nombre} onChange={e=>f('nombre', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Tipo</label><select className="form-select" value={form.tipo} onChange={e=>f('tipo', e.target.value)}><option value="activo">Activo</option><option value="poco_activo">Poco activo</option></select></div>
                <div className="form-group"><label className="form-label">Cédula</label><input className="form-input" value={form.cedula || ''} onChange={e=>f('cedula', e.target.value)} disabled={!!linkedUser} style={linkedUser ? { opacity:0.6 } : undefined} /></div>
                <div className="form-group"><label className="form-label">Vencimiento DIP <span style={{color:'var(--silver)',fontWeight:400}}>(cédula)</span></label><input className="form-input" type="date" value={form.dip_vencimiento || ''} onChange={e=>f('dip_vencimiento', e.target.value)} disabled={!!linkedUser} style={linkedUser ? { opacity:0.6 } : undefined} /></div>
                {linkedUser && (
                  <div className="form-group" style={{ gridColumn:'span 2', margin:0 }}>
                    <div style={{ fontSize:11, color:'var(--text-warning)', display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      <span>🔒 La cédula y su vencimiento se gestionan desde el usuario vinculado <strong>{linkedUser.nombre}</strong> (Admin → Usuarios).</span>
                      {form.cedula_url && (
                        <a href="#" onClick={(e)=>{ e.preventDefault(); abrirDocumento(form.cedula_url); }} style={{ color:'var(--sky-light)', display:'inline-flex', alignItems:'center', gap:4 }}>
                          <FileText size={12}/> Ver copia de cédula
                        </a>
                      )}
                    </div>
                  </div>
                )}
                <div className="form-group"><label className="form-label">Teléfono</label><input className="form-input" value={form.telefono || ''} onChange={e=>f('telefono', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email || ''} onChange={e=>f('email', e.target.value)} /></div>
                <div className="divider" style={{ gridColumn:'span 2' }} />
                <div style={{ gridColumn:'span 2', fontSize:13, fontWeight:600, color:'var(--silver)' }}>COMISIONES — PÓLIZAS NUEVAS</div>
                <div className="form-group"><label className="form-label">% Agente (nuevo)</label><input className="form-input" type="number" min={0} max={100} value={form.comision_nueva_agente} onChange={e=>{ f('comision_nueva_agente', e.target.value); f('comision_nueva_ru', 100 - parseFloat(e.target.value || 0)); }} /></div>
                <div className="form-group"><label className="form-label">% Riesgo Unido</label><input className="form-input" type="number" value={form.comision_nueva_ru} readOnly style={{ opacity:0.6 }} /></div>
                <div style={{ gridColumn:'span 2', fontSize:13, fontWeight:600, color:'var(--silver)' }}>COMISIONES — RENOVACIONES</div>
                <div className="form-group"><label className="form-label">% Agente (renovación)</label><input className="form-input" type="number" min={0} max={100} value={form.comision_renovacion_agente} onChange={e=>{ f('comision_renovacion_agente', e.target.value); f('comision_renovacion_ru', 100 - parseFloat(e.target.value || 0)); }} /></div>
                <div className="form-group"><label className="form-label">% Riesgo Unido</label><input className="form-input" type="number" value={form.comision_renovacion_ru} readOnly style={{ opacity:0.6 }} /></div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setModal(null)}>Cancelar</button><button className="btn btn-primary" onClick={handleSave}>{modal === 'create' ? 'Crear Agente' : 'Guardar'}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
